import React, { useMemo, useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useStore } from '../context/StoreContext';
import { DEFAULT_NOTE_COLORS } from '../constants';
import SafeIcon from '../common/SafeIcon';
import { htmlToPlainText, plainTextToHtml, sanitizeRichHtml } from '../utils/richText';
import { formatFileSize } from '../utils/attachment';
import { copyNote, recordRecentNote } from '../utils/productivity';
import { extractTextFromImage } from '../utils/ocr';

const REVEAL_PX = 112;
const CLAMP_PX = 132;
const SNAP_OPEN_THRESHOLD_PX = 56;
const SNAP_CLOSE_THRESHOLD_PX = 24;
const VELOCITY_OPEN_PX_PER_MS = 0.45;
const ANIM_MS = 180;
const TABLET_BREAKPOINT_PX = 1099;

const NoteCard = ({ note, showTrashAction = false, activeSwipeId, setActiveSwipeId }) => {
  const {
    setEditingNote, togglePin, openConfirm, softDeleteNote,
    selectedNoteIds, setSelectedNoteIds, archiveNote, saveNote
  } = useStore();

  const [isHovered, setIsHovered] = useState(false);
  const [copied, setCopied] = useState(false);
  const [ocrState, setOcrState] = useState({ busy: false, progress: 0, error: false });
  const [dragX, setDragX] = useState(0);
  const [snapState, setSnapState] = useState('closed');
  const [isAnimating, setIsAnimating] = useState(false);
  const cardRef = useRef(null);
  const isDragging = useRef(false);
  const startPos = useRef({ x: 0, y: 0, time: 0, baseX: 0 });
  const isMobile = window.innerWidth <= TABLET_BREAKPOINT_PX;
  const isSelected = selectedNoteIds.includes(note.id);
  const attachments = note.attachments || [];
  const imageAttachments = attachments.filter(att => att.type === 'image');
  const fileAttachments = attachments.filter(att => att.type !== 'image');
  const textPreview = note.bodyHtml ? htmlToPlainText(note.bodyHtml) : (note.bodyText || '');

  useEffect(() => {
    if (activeSwipeId && activeSwipeId !== note.id && snapState !== 'closed') {
      setSnapState('closed');
      setDragX(0);
    }
  }, [activeSwipeId, note.id, snapState]);

  const handlePointerDown = e => {
    if (!isMobile) return;
    let currentBaseX = 0;
    if (snapState === 'leftOpen') currentBaseX = -REVEAL_PX;
    if (snapState === 'rightOpen') currentBaseX = REVEAL_PX;
    isDragging.current = true;
    startPos.current = { x: e.clientX, y: e.clientY, time: performance.now(), baseX: currentBaseX };
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsAnimating(false);
  };

  const handlePointerMove = e => {
    if (!isDragging.current) return;
    const dx = e.clientX - startPos.current.x;
    const dy = e.clientY - startPos.current.y;
    if (Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(dy)) {
      if (e.cancelable) e.preventDefault();
      setDragX(dx);
      if (snapState === 'closed' && Math.abs(dx) > 10) setActiveSwipeId?.(note.id);
    }
  };

  const handlePointerUp = e => {
    if (!isDragging.current) return;
    isDragging.current = false;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
    const dt = Math.max(1, performance.now() - startPos.current.time);
    const dx = e.clientX - startPos.current.x;
    const velocity = dx / dt;
    const finalX = startPos.current.baseX + dx;
    setIsAnimating(true);
    setDragX(0);

    if (snapState === 'closed') {
      if (finalX <= -SNAP_OPEN_THRESHOLD_PX || velocity <= -VELOCITY_OPEN_PX_PER_MS) setSnapState('leftOpen');
      else if (finalX >= SNAP_OPEN_THRESHOLD_PX || velocity >= VELOCITY_OPEN_PX_PER_MS) setSnapState('rightOpen');
      else { setSnapState('closed'); setActiveSwipeId?.(null); }
    } else if (snapState === 'leftOpen') {
      if (finalX > -SNAP_CLOSE_THRESHOLD_PX || velocity >= VELOCITY_OPEN_PX_PER_MS) { setSnapState('closed'); setActiveSwipeId?.(null); }
      else setSnapState('leftOpen');
    } else if (snapState === 'rightOpen') {
      if (finalX < SNAP_CLOSE_THRESHOLD_PX || velocity <= -VELOCITY_OPEN_PX_PER_MS) { setSnapState('closed'); setActiveSwipeId?.(null); }
      else setSnapState('rightOpen');
    }
  };

  const effectiveX = useMemo(() => {
    let baseX = 0;
    if (snapState === 'leftOpen') baseX = -REVEAL_PX;
    if (snapState === 'rightOpen') baseX = REVEAL_PX;
    return Math.max(-CLAMP_PX, Math.min(CLAMP_PX, baseX + dragX));
  }, [snapState, dragX]);

  const colorObj = DEFAULT_NOTE_COLORS.find(c => c.key === note.colorKey) || DEFAULT_NOTE_COLORS[5];

  const handleToggleSelect = e => {
    e?.stopPropagation();
    setSelectedNoteIds(prev => prev.includes(note.id) ? prev.filter(id => id !== note.id) : [...prev, note.id]);
    setSnapState('closed');
  };

  const handleArchive = e => {
    e?.stopPropagation();
    archiveNote(note.id);
    setSnapState('closed');
  };

  const handleTrashClick = e => {
    e?.stopPropagation();
    openConfirm({
      title: 'Move to Trash?',
      message: 'This note will be moved to your Trash area in the Dashboard. You can restore it later.',
      danger: true,
      confirmLabel: 'Move to Trash',
      onConfirm: () => softDeleteNote(note.id)
    });
    setSnapState('closed');
  };

  const handleCardClick = () => {
    if (snapState !== 'closed') {
      setSnapState('closed');
      setActiveSwipeId?.(null);
      return;
    }
    recordRecentNote(note.id);
    setEditingNote(note);
  };

  const handleCopy = async e => {
    e.stopPropagation();
    if (await copyNote(note, note.bodyHtml ? 'rich' : 'plain')) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }
  };

  const handleFavorite = async e => {
    e.stopPropagation();
    await saveNote({ ...note, favorite: !note.favorite }, true);
  };

  const handleOcr = async e => {
    e.stopPropagation();
    if (ocrState.busy || !imageAttachments.length) return;
    const attachment = imageAttachments.find(att => att.dataRef) || imageAttachments[0];
    const source = attachment?.dataRef || attachment?.driveUrl;
    if (!source) return;
    setOcrState({ busy: true, progress: 0, error: false });
    try {
      const text = await extractTextFromImage(source, progress => setOcrState({ busy: true, progress, error: false }));
      if (!text) throw new Error('No text detected');
      const bodyText = [note.bodyText || '', 'Extracted text', text].filter(Boolean).join('\n\n');
      const bodyHtml = note.bodyHtml
        ? `${sanitizeRichHtml(note.bodyHtml)}<div><br></div><div><strong>Extracted text</strong></div>${plainTextToHtml(text)}`
        : undefined;
      await saveNote({ ...note, bodyText, bodyHtml, ocrUpdatedAt: Date.now() }, true);
      setOcrState({ busy: false, progress: 100, error: false });
      setTimeout(() => setOcrState({ busy: false, progress: 0, error: false }), 1600);
    } catch (err) {
      console.warn('OCR failed', err);
      setOcrState({ busy: false, progress: 0, error: true });
      setTimeout(() => setOcrState({ busy: false, progress: 0, error: false }), 2200);
    }
  };

  const handleDragStart = e => {
    if (isMobile) return;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/x-stickyhq-note-id', note.id);
    e.dataTransfer.setData('text/plain', note.id);
  };

  const previewPosition = () => {
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return { top: 16, left: 16 };
    const width = 400;
    const gap = 18;
    const left = rect.right + gap + width <= window.innerWidth ? rect.right + gap : Math.max(16, rect.left - width - gap);
    return { top: Math.max(16, Math.min(rect.top, window.innerHeight - 520)), left };
  };

  return (
    <div className="relative overflow-hidden rounded-2xl mb-6" ref={cardRef}>
      {isMobile && (
        <div className="absolute inset-0 flex justify-between items-stretch">
          <div className="flex">
            <button onClick={handleToggleSelect} className={`w-[56px] flex items-center justify-center text-white ${isSelected ? 'bg-blue-600' : 'bg-blue-500'}`}><SafeIcon name={isSelected ? 'CheckCircle' : 'Circle'} /></button>
            <button onClick={(e) => { e.stopPropagation(); togglePin(note); setSnapState('closed'); }} className={`w-[56px] flex items-center justify-center text-white ${note.pinned ? 'bg-yellow-600' : 'bg-yellow-500'}`}><SafeIcon name="Star" className={note.pinned ? 'fill-current' : ''} /></button>
          </div>
          <div className="flex">
            <button onClick={handleArchive} className="w-[56px] flex items-center justify-center bg-orange-500 text-white"><SafeIcon name="Archive" /></button>
            <button onClick={handleTrashClick} className="w-[56px] flex items-center justify-center bg-red-500 text-white"><SafeIcon name="Trash2" /></button>
          </div>
        </div>
      )}

      <div
        draggable={!isMobile}
        onDragStart={handleDragStart}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => !isMobile && setIsHovered(false)}
        onClick={handleCardClick}
        className={`relative p-5 rounded-2xl shadow-sm hover:shadow-md border border-black/5 text-gray-800 z-10 select-none touch-pan-y ${isSelected ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}
        style={{ backgroundColor: colorObj.hex, transform: `translateX(${effectiveX}px)`, transition: isAnimating ? `transform ${ANIM_MS}ms cubic-bezier(0.2, 0, 0, 1)` : 'none', cursor: 'grab' }}
      >
        <div className="flex justify-between items-start mb-2 group">
          <div className="min-w-0 pr-8">
            <div className="flex items-center gap-2">
              {note.favorite && <SafeIcon name="Heart" className="text-rose-500 fill-current shrink-0" />}
              {note.inbox && <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Inbox</span>}
            </div>
            <h3 className="font-bold text-lg leading-tight mt-1 truncate">{note.title || 'Untitled'}</h3>
          </div>
          <div className="absolute top-4 right-4 flex gap-1 opacity-0 md:group-hover:opacity-100 transition-opacity">
            {showTrashAction && !isMobile && <button onClick={handleTrashClick} className="p-1.5 rounded-full bg-black/5 hover:bg-red-500 hover:text-white"><SafeIcon name="Trash2" className="text-xs" /></button>}
            {!isMobile && <button onClick={(e) => { e.stopPropagation(); togglePin(note); }} className="p-1.5 rounded-full bg-black/5 hover:bg-black/10"><SafeIcon name="Star" className={note.pinned ? 'fill-current text-yellow-500' : ''} /></button>}
          </div>
        </div>

        <div className="text-sm leading-relaxed whitespace-pre-wrap line-clamp-6 text-gray-600">
          {note.type === 'text' && (textPreview || 'Empty note…')}
          {note.type === 'checklist' && <div className="space-y-1 mt-2">{note.checklistItems?.slice(0, 4).map(item => <div key={item.id} className="flex items-center gap-2"><SafeIcon name={item.done ? 'CheckSquare' : 'Square'} className="w-3.5 h-3.5 opacity-70" /><span className={item.done ? 'line-through opacity-70' : ''}>{item.text}</span></div>)}</div>}
          {note.type === 'table' && <span className="opacity-70">Table note · {note.table?.rows?.length || 0} rows</span>}
        </div>

        {attachments.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-black/5 text-[10px] font-bold uppercase"><SafeIcon name="Paperclip" /> {attachments.length}</span>
            {imageAttachments.length > 0 && <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-black/5 text-[10px] font-bold uppercase"><SafeIcon name="Image" /> {imageAttachments.length}</span>}
            {fileAttachments.length > 0 && <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-black/5 text-[10px] font-bold uppercase"><SafeIcon name="FileText" /> {fileAttachments.length}</span>}
          </div>
        )}

        {note.tags?.length > 0 && <div className="mt-4 flex flex-wrap gap-2">{note.tags.map(tag => <span key={tag} className="px-2 py-0.5 rounded-md bg-black/5 text-[10px] font-bold uppercase">#{tag}</span>)}</div>}

        <div className="mt-4 pt-3 border-t border-black/5 flex items-center justify-between gap-2">
          <span className="text-[10px] uppercase font-bold tracking-wider opacity-40">{new Date(note.updatedAt).toLocaleDateString()}</span>
          <div className="flex items-center gap-1">
            {imageAttachments.length > 0 && (
              <button onClick={handleOcr} className={`min-w-11 min-h-11 p-2.5 rounded-xl transition ${ocrState.error ? 'text-red-600 bg-red-50' : 'text-gray-500 hover:bg-black/5'}`} title={ocrState.busy ? `Extracting text ${ocrState.progress}%` : ocrState.error ? 'OCR failed — local image data may be unavailable' : 'Extract text from screenshot'}>
                <SafeIcon name={ocrState.busy ? 'Loader' : ocrState.error ? 'AlertCircle' : 'Search'} className={ocrState.busy ? 'animate-spin' : ''} />
              </button>
            )}
            <button onClick={handleFavorite} className={`min-w-11 min-h-11 p-2.5 rounded-xl hover:bg-black/5 ${note.favorite ? 'text-rose-500' : 'text-gray-500'}`} title={note.favorite ? 'Remove from Favorites' : 'Add to Favorites'}><SafeIcon name="Heart" className={note.favorite ? 'fill-current' : ''} /></button>
            <button onClick={handleCopy} className={`min-w-11 min-h-11 p-2.5 rounded-xl hover:bg-black/5 ${copied ? 'text-green-600' : 'text-gray-500'}`} title="Copy note"><SafeIcon name={copied ? 'Check' : 'Copy'} /></button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isHovered && !isMobile && !showTrashAction && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 4 }}
            transition={{ duration: 0.14 }}
            className="fixed z-[100] w-[400px] bg-white rounded-3xl shadow-2xl border border-gray-100 p-6 pointer-events-none hidden lg:block"
            style={previewPosition()}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-blue-600 bg-blue-50 shrink-0"><SafeIcon name={note.type === 'checklist' ? 'CheckSquare' : note.type === 'table' ? 'Grid' : 'FileText'} /></div>
                <div className="min-w-0"><h4 className="text-lg font-black text-gray-900 leading-tight truncate">{note.title || 'Untitled'}</h4><div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Quick preview</div></div>
              </div>
              {attachments.length > 0 && <span className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-50 text-blue-600 text-[10px] font-bold"><SafeIcon name="Paperclip" />{attachments.length}</span>}
            </div>

            <div className="text-sm text-gray-600 leading-relaxed max-h-[15rem] overflow-hidden">
              {note.type === 'text' && note.bodyHtml ? <div className="rich-note-preview" dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(note.bodyHtml) }} /> : note.type === 'text' ? <div className="whitespace-pre-wrap">{note.bodyText || 'Start typing in this note…'}</div> : note.type === 'checklist' ? <div className="space-y-2">{note.checklistItems?.slice(0, 10).map(item => <div key={item.id} className="flex items-center gap-3"><SafeIcon name={item.done ? 'CheckCircle' : 'Circle'} className={`w-4 h-4 ${item.done ? 'text-green-500' : 'text-gray-300'}`} /><span className={item.done ? 'line-through opacity-50' : ''}>{item.text}</span></div>)}</div> : <div className="text-gray-500">Table with {note.table?.rows?.length || 0} rows and {note.table?.columns?.length || 0} columns.</div>}
            </div>

            {attachments.length > 0 && (
              <div className="mt-5 pt-4 border-t border-gray-100 space-y-2">
                <div className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Attachments</div>
                {attachments.slice(0, 3).map(att => (
                  <div key={att.id} className="flex items-center gap-2 min-w-0">
                    {att.type === 'image' && (att.dataRef || att.driveUrl) ? <img src={att.dataRef || att.driveUrl} alt="" className="w-8 h-8 rounded-md object-cover bg-gray-100 shrink-0" /> : <div className="w-8 h-8 rounded-md bg-gray-100 text-gray-500 flex items-center justify-center shrink-0"><SafeIcon name="FileText" /></div>}
                    <div className="min-w-0 flex-1"><div className="text-xs font-bold text-gray-700 truncate">{att.name || att.caption || (att.type === 'image' ? 'Screenshot' : 'Attachment')}</div><div className="text-[10px] text-gray-400">{formatFileSize(att.size)}</div></div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-gray-400"><span>Updated {new Date(note.updatedAt).toLocaleTimeString()}</span><span className="text-blue-500">Click to edit</span></div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NoteCard;